import React, { useCallback, useEffect, useState } from 'react';
import { Copy, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { CodeInput } from './auth/MfaStep';

const detail = (error, fallback) => error.response?.data?.detail || fallback;

const RecoveryCodes = ({ codes }) => (
  <div className="space-y-2">
    <p className="text-sm text-gray-600">Each recovery code signs you in once if you lose your phone. Save them somewhere safe; they will not be shown again.</p>
    <ul className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border bg-gray-50 p-3 font-mono text-sm" data-testid="settings-recovery-codes">{codes.map((code) => <li key={code}>{code}</li>)}</ul>
    <Button type="button" variant="outline" size="sm" onClick={() => { navigator.clipboard?.writeText(codes.join('\n')); toast.success('Recovery codes copied'); }}><Copy size={14} className="mr-1" />Copy codes</Button>
  </div>
);

export default function TwoStepVerificationDialog({ open, onOpenChange }) {
  const [status, setStatus] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [codes, setCodes] = useState(null);
  const [code, setCode] = useState('');
  const [action, setAction] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/auth/mfa/status');
      setStatus(data);
    } catch (error) {
      toast.error(detail(error, 'Could not load two-step verification settings'));
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setEnrollment(null); setCodes(null); setCode(''); setAction('');
    load();
  }, [open, load]);

  const run = async (request) => {
    if (busy) return;
    setBusy(true);
    try { await request(); } finally { setBusy(false); setCode(''); }
  };

  const startEnrollment = () => run(async () => {
    try {
      const { data } = await axiosInstance.post('/auth/mfa/enroll/start');
      setEnrollment(data);
    } catch (error) { toast.error(detail(error, 'Could not start set-up')); }
  });

  const confirmEnrollment = (value = code) => run(async () => {
    try {
      const { data } = await axiosInstance.post('/auth/mfa/enroll/confirm', { code: value });
      if (data.token) localStorage.setItem('token', data.token);
      setEnrollment(null); setCodes(data.recovery_codes);
      toast.success('Two-step verification is on. Other sessions were signed out.');
      load();
    } catch (error) { toast.error(detail(error, 'That code is not valid')); }
  });

  const confirmAction = (value = code) => run(async () => {
    try {
      if (action === 'regenerate') {
        const { data } = await axiosInstance.post('/auth/mfa/recovery-codes', { code: value });
        setCodes(data.recovery_codes);
        toast.success('New recovery codes created. The old ones no longer work.');
      } else {
        const { data } = await axiosInstance.post('/auth/mfa/disable', { code: value });
        if (data.token) localStorage.setItem('token', data.token);
        toast.success('Two-step verification is off');
      }
      setAction('');
      load();
    } catch (error) { toast.error(detail(error, 'That code is not valid')); }
  });

  const codeForm = (label, onSubmit) => (
    <form className="space-y-3" onSubmit={(event) => { event.preventDefault(); if (code.length === 6) onSubmit(); }}>
      <label htmlFor="settings-mfa-code" className="block text-sm font-medium">{label}</label>
      <CodeInput id="settings-mfa-code" value={code} onChange={setCode} onComplete={onSubmit} containerClassName="" slotClassName="" />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => { setEnrollment(null); setAction(''); setCode(''); }}>Cancel</Button>
        <Button type="submit" disabled={busy || code.length !== 6}>{busy ? 'Checking…' : 'Confirm'}</Button>
      </div>
    </form>
  );

  let body = <p className="text-sm text-gray-500">Loading…</p>;
  if (codes) {
    body = <><RecoveryCodes codes={codes} /><div className="flex justify-end"><Button onClick={() => setCodes(null)}>Done</Button></div></>;
  } else if (enrollment) {
    body = <div className="space-y-3">
      <p className="text-sm text-gray-600">Scan this with your authenticator app, then enter the 6-digit code it shows.</p>
      <img src={enrollment.qr_svg} alt="QR code for your authenticator app" width="180" height="180" className="mx-auto rounded-md bg-white" />
      <p className="text-center text-xs text-gray-500">Can't scan it? Enter <code className="font-mono text-gray-800">{enrollment.secret.match(/.{1,4}/g).join(' ')}</code></p>
      {codeForm('6-digit code', confirmEnrollment)}
    </div>;
  } else if (action) {
    body = codeForm(action === 'regenerate' ? 'Enter a code from your app to create new recovery codes' : 'Enter a code from your app to turn two-step verification off', confirmAction);
  } else if (status) {
    body = <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-md border p-3">
        <ShieldCheck size={20} className={status.enabled ? 'text-emerald-600' : 'text-gray-400'} aria-hidden="true" />
        <div className="text-sm">
          <p className="font-medium text-gray-900">{status.enabled ? 'On' : 'Off'}</p>
          <p className="text-gray-600">{status.enabled
            ? `Signing in asks for a code from your authenticator app. ${status.recovery_codes_remaining} recovery code${status.recovery_codes_remaining === 1 ? '' : 's'} left.`
            : 'Add a code from an authenticator app to every sign-in.'}</p>
          {status.required && <p className="mt-1 text-xs text-amber-700">Required for your account{status.organization_required ? ' by your organisation' : ''}.</p>}
        </div>
      </div>
      {!status.available && !status.enabled && <p className="text-sm text-amber-700">Two-step verification is not configured on this environment yet.</p>}
      <div className="flex flex-wrap justify-end gap-2">
        {status.enabled && <Button variant="outline" onClick={() => setAction('regenerate')}>New recovery codes</Button>}
        {status.enabled && !status.required && <Button variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => setAction('disable')}>Turn off</Button>}
        {!status.enabled && <Button onClick={startEnrollment} disabled={busy || !status.available} data-testid="settings-mfa-enable">Set up</Button>}
      </div>
    </div>;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="two-step-dialog">
        <DialogHeader><DialogTitle>Two-step verification</DialogTitle></DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
